import { useState } from 'react'
import { Outlet, useLocation, Navigate, useNavigate } from 'react-router'
import { PodusCheckerSidebar } from '../components/Sidebar'
import InvestigationFlow from '../components/modals/InvestigationFlow'
import SubmitReportModal from '../components/modals/SubmitReportModal'
import SupportModal from '../components/modals/SupportModal'
import { useAppSelector } from '../hooks/useAppStore'
import { selectCurrentUser, selectIsAuthenticated } from '../store/slices/authSlice'
import { UserRole } from '../types'

export default function AuthenticatedLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const currentUser = useAppSelector(selectCurrentUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const [isInvestigationFlowOpen, setIsInvestigationFlowOpen] = useState(false)
  const [isSubmitReportModalOpen, setIsSubmitReportModalOpen] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)

  const isAgent = currentUser?.role === UserRole.AGENT
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Show loading state while user profile is being loaded
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EDEFF3]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Determine active item based on current pathname
  const getActiveItem = (pathname: string) => {
    if (pathname.startsWith('/dashboard')) return 'dashboard'
    if (pathname.startsWith('/investigations')) return 'investigations'
    if (pathname.startsWith('/transactions')) return 'transactions'
    if (pathname.startsWith('/earnings')) return 'earnings'
    if (pathname.startsWith('/users')) return 'users'
    if (pathname.startsWith('/settings')) return 'settings'
    if (pathname.startsWith('/support')) return 'support'
    return 'dashboard' // default
  }

  const activeItem = getActiveItem(location.pathname)

  const handleRequestInvestigation = () => {
    if (isAgent) {
      setIsSubmitReportModalOpen(true)
    } else {
      setIsInvestigationFlowOpen(true)
    }
  }

  const handleSupport = () => {
    if (isSuperAdmin) {
      navigate('/support')
    } else {
      setIsSupportModalOpen(true)
    }
  }

  return (
    <div className='flex min-h-screen bg-[#EDEFF3]'>
      {/* Sidebar */}
      <PodusCheckerSidebar 
        activeItem={activeItem} 
        onRequestInvestigation={handleRequestInvestigation}
        onSupport={handleSupport}
        userRole={currentUser?.role}
      />

      {/* Main Content */}
      <div className='flex-1 flex flex-col w-full lg:ml-[300px]'>
        {/* Page Content */}
        <main className='flex-1 overflow-y-auto w-full pt-14 lg:pt-0'>
          <Outlet context={{ onRequestInvestigation: () => setIsInvestigationFlowOpen(true) }} />
        </main>
      </div>

      {/* Investigation Flow Modals */}
      <InvestigationFlow 
        isOpen={isInvestigationFlowOpen}
        onClose={() => setIsInvestigationFlowOpen(false)}
      />

      {/* Submit Report Modal for Agents */}
      <SubmitReportModal
        isOpen={isSubmitReportModalOpen}
        onClose={() => setIsSubmitReportModalOpen(false)}
        investigationId=""
      />

      {/* Support Modal for non-admins */}
      {!isSuperAdmin && (
        <SupportModal
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
        />
      )}
    </div>
  )
}
